// src/components/FroggyChatBubble/chatMarkdown.jsx
//
// THE ONE RENDERER FOR AN ASSISTANT'S ANSWER, shared by the customer
// FroggyAI bubble and the FroggerAI dock (Cowork run 24, finding 8: the
// dock printed Markdown `**bold**` as raw asterisks while the bubble drew
// it bold). Lifted out of `FroggyChatBubble.jsx` BYTE FOR BYTE — the rules
// Cowork QA validated there (Runs 31–34, 2026-05-25) are the rules here,
// and the image-host whitelist is a security rule (HIGH-S1, 2026-05-23),
// so there is one copy of it for both surfaces rather than two.
//
// ⚠️ NO RAW HTML. `react-markdown` renders none unless a rehype-raw
// plugin is passed, and none is: a model's `<b>` is text, never markup.
//
// `pages/FroggyChatPageStreaming.jsx` still carries its own duplicate (the
// bubble's header records that as a known debt); it is untouched here.
import React from 'react';
import ReactMarkdown from 'react-markdown';

export const ALLOWED_IMAGE_HOST_REGEXES = [
  /^https:\/\/(cdn|media|images?)(?:-(?:beta|staging|dev|preview))?\.feelingfroggy\.llc(\/|$)/i,
  /^https:\/\/feelingfroggy\.llc\/static\//i,
  /^https:\/\/ids\.si\.edu\//i,
  /^https:\/\/[a-z0-9-]+\.si\.edu\//i,
];

export function isAllowedImageHost(src) {
  if (!src || typeof src !== 'string') return false;
  return ALLOWED_IMAGE_HOST_REGEXES.some((re) => re.test(src));
}

export const MARKDOWN_COMPONENTS = {
  a: ({ node, href, children, ...props }) => {
    const isExternal = /^https?:\/\//i.test(href || '');
    return (
      <a
        href={href}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...props}
      >
        {children}
      </a>
    );
  },
  img: ({ node, src, alt, ...props }) => {
    if (!isAllowedImageHost(src)) return null;
    return (
      <img
        src={src}
        alt={alt || ''}
        loading="lazy"
        referrerPolicy="no-referrer"
        className="froggy-bubble-img"
        {...props}
      />
    );
  },
};

/**
 * ⚠️ **A LINE THE MODEL ENDED IS A LINE ON SCREEN** (Cowork run 26, F4c).
 *
 * "who is in QADELETE g1?" came back as a column of bold names, one per
 * line — `**A** (male)\n**B** (female)` — and Markdown reads a SINGLE
 * newline inside a paragraph as a soft break, i.e. a space: the names ran
 * together on one line with nothing between them. The fragments were
 * assembled correctly (run 25); it was the rendering that dropped the
 * structure. This puts two trailing spaces (CommonMark's own spelling of
 * a hard break) on EVERY non-blank line that is followed by another
 * non-blank line, outside fenced code — list items, headings and
 * indented-code lines included. On those three the spaces are harmless
 * under CommonMark: a heading's trailing spaces are stripped, a hard
 * break at the end of a list item's last line (before the next item)
 * renders as nothing, and two spaces at the end of an indented-code line
 * are invisible. A line followed by a blank line (the end of a paragraph)
 * and the last line are left as they were, and a line that already ends
 * in two spaces is not given more.
 */
export const keepLineBreaks = (text) => {
  const lines = String(text || '').split('\n');
  let fenced = false;
  return lines.map((line, index) => {
    if (/^\s*(```|~~~)/.test(line)) {
      fenced = !fenced;
      return line;
    }
    const next = lines[index + 1];
    if (fenced || next === undefined || !line.trim() || !next.trim()) {
      return line;
    }
    return /\s{2}$/.test(line) ? line : `${line.replace(/\s+$/, '')}  `;
  }).join('\n');
};

/** An assistant's text, as Markdown: bold, italics, lists, links.
 *
 * `lineBreaks` keeps the model's single newlines as line breaks
 * (`keepLineBreaks`). The FroggerAI dock passes it; the customer bubble
 * does not, so its validated rendering is byte-identical. */
export function ChatMarkdown({ children, lineBreaks }) {
  const text = children || '';
  return (
    <ReactMarkdown components={MARKDOWN_COMPONENTS}>
      {lineBreaks ? keepLineBreaks(text) : text}
    </ReactMarkdown>
  );
}
